// BM-H5047 (Vanessa's spec, 4 Nov 2026): the supervision board, Phase 1.
//
// "A pin board inside CaseMinder that shows, at a glance, every piece of
// trainee work waiting for Vanessa, what she has sent back, and what has
// gone out, per department and per matter."
//
// Two boards (Company Commercial, Litigation) and an All view. Five columns;
// a swimlane per matter, sorted by the nearest due date. Opening a card shows
// the document link on the left and the comment thread on the right, with
// Approve / Changes needed for the approver and the owner's own moves. The
// board refreshes itself every few seconds (live push is Phase 4).
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ClipboardCheck, Mail, FileText, BookOpen, StickyNote, File, MessageSquare,
  ChevronDown, ChevronRight, ExternalLink, Check, Undo2, Send, Loader2, Gavel, Users, Plus, AlertTriangle,
} from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import SubmitForReviewDialog, { isOutlookLink } from "../components/SubmitForReviewDialog";
import RichText, { FormattedTextarea } from "../components/supervision/RichText";

const REFRESH_MS = 4000;

const TYPE_ICONS = {
  letter: FileText, email: Mail, court: Gavel, advice: BookOpen, memo: StickyNote, other: File,
};

const BOARDS = [
  { key: "all", label: "All" },
  { key: "coco", label: "Company Commercial" },
  { key: "litigation", label: "Litigation" },
];

// Status colours only for status (UI rule): red is due/overdue, amber soon.
export function dueChip(due, today = new Date()) {
  if (!due) return null;
  const d = new Date(`${due}T23:59:59`);
  const hours = (d - today) / 36e5;
  const label = new Date(`${due}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  if (hours <= 24) return { tone: "red", label: hours < 0 ? `Overdue ${label}` : `Due ${label}` };
  if (hours <= 72) return { tone: "amber", label: `Due ${label}` };
  return { tone: "plain", label: `Due ${label}` };
}

export function ageLabel(card, approvers = []) {
  const days = card.days_in_column ?? 0;
  const n = days < 1 ? "today" : `${Math.floor(days)} day${Math.floor(days) === 1 ? "" : "s"}`;
  const who = approvers[0] ? approvers[0].split(" ")[0] : "reviewer";
  if (card.column === "ready_for_review") return days < 1 ? `With ${who} since today` : `${n} with ${who}`;
  if (card.column === "changes_needed") return days < 1 ? "Back since today" : `Back ${n}`;
  return days < 1 ? "Since today" : `${n} here`;
}

const toneCls = {
  red: "bg-status-red/20 text-red-200 ring-1 ring-red-400/40",
  amber: "bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/40",
  plain: "bg-white/5 text-slate-300 ring-1 ring-white/10",
};

// Trello-style colour coding (Vanessa, 4 Nov 2026: "colour coding a la
// Trello would be really helpful"). Three layers, each readable from across
// a desk:
//   * a coloured label bar per kind of item (letter, email, court ...);
//   * a colour per person on the avatar, so whose card it is shows at once;
//   * the column's status colour on the card's left edge and column header.
// House UI rule: green, amber and red mean status only, so they are used for
// the columns (Approved, Changes needed) and never for a label or a person.
export const TYPE_LABELS = {
  letter: { label: "Letter", cls: "bg-sky-500" },
  email: { label: "Email", cls: "bg-violet-500" },
  advice: { label: "Advice", cls: "bg-pink-400" },
  memo: { label: "Memo", cls: "bg-slate-400" },
  court: { label: "Court", cls: "bg-fuchsia-500" },
  other: { label: "Other", cls: "bg-blue-400" },
};

const PERSON_HUES = ["bg-indigo-600", "bg-fuchsia-600", "bg-sky-600", "bg-violet-600", "bg-pink-600", "bg-blue-600"];

export function personHue(initials = "") {
  let h = 0;
  for (const ch of initials) h = (h * 31 + ch.charCodeAt(0)) % 9973;
  return PERSON_HUES[h % PERSON_HUES.length];
}

export const COLUMN_HUES = {
  drafting: { edge: "border-l-slate-400", head: "bg-slate-400", top: "border-t-slate-400" },
  ready_for_review: { edge: "border-l-indigo-400", head: "bg-indigo-400", top: "border-t-indigo-400" },
  changes_needed: { edge: "border-l-status-amber", head: "bg-status-amber", top: "border-t-status-amber" },
  approved: { edge: "border-l-status-green", head: "bg-status-green", top: "border-t-status-green" },
  sent: { edge: "border-l-slate-600", head: "bg-slate-600", top: "border-t-slate-600" },
};

// Vanessa, 4 Nov: matters with work waiting for her always sit above
// matters where everything is already approved or sent. Within a rank the
// server's order (nearest due date first) is kept.
const LANE_RANK = { ready_for_review: 0, changes_needed: 1, drafting: 2, approved: 3, sent: 4 };

export function laneRank(lane) {
  return Math.min(5, ...(lane.cards || []).map(c => LANE_RANK[c.column] ?? 5));
}

export function orderLanes(lanes) {
  return (lanes || []).map((l, i) => [l, i])
    .sort(([a, i], [b, j]) => laneRank(a) - laneRank(b) || i - j)
    .map(([l]) => l);
}

function CardTile({ card, approvers, onOpen }) {
  const Icon = TYPE_ICONS[card.item_type] || File;
  const chip = dueChip(card.due_date);
  const typeLabel = TYPE_LABELS[card.item_type] || TYPE_LABELS.other;
  return (
    <button type="button" onClick={() => onOpen(card)} data-testid={`sv-card-${card.id}`}
      className={`w-full text-left rounded-lg border border-l-4 ${(COLUMN_HUES[card.column] || {}).edge || ""} px-2.5 py-2 mb-2 bg-slate-900/70 hover:bg-slate-800/80 transition-colors ${
        card.overdue_review ? "border-red-400/50" : "border-white/10"}`}>
      <div className="flex flex-wrap gap-1 mb-1.5" data-testid="sv-card-labels">
        <span className={`h-2 w-10 rounded-full ${typeLabel.cls}`} title={typeLabel.label} data-testid={`sv-label-${card.item_type}`} />
        {card.client_facing && !card.court && <span className="h-2 w-10 rounded-full bg-cyan-400" title="Client-facing" />}
      </div>
      <div className="flex items-start gap-2">
        <span className={`shrink-0 w-7 h-7 rounded-full ${personHue(card.owner_initials)} text-white text-[11px] font-bold flex items-center justify-center`}
          title={card.owner_name}>{card.owner_initials}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[13px] font-medium text-white leading-snug">
            <Icon className="w-3.5 h-3.5 shrink-0 text-indigo-300" aria-label={card.item_type} />
            <span className="truncate">{card.title}</span>
          </div>
          <div className="text-[11px] text-slate-400 truncate">{card.matter_reference} · {card.client_name || "client not on record"}</div>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[10px]">
        {chip && <span className={`px-1.5 py-0.5 rounded ${toneCls[chip.tone]}`}>{chip.label}</span>}
        <span className={`px-1.5 py-0.5 rounded ${card.overdue_review ? toneCls.red : toneCls.plain}`}
          data-testid="sv-card-age">{ageLabel(card, approvers)}</span>
        {card.version > 1 && <span className="px-1.5 py-0.5 rounded bg-white/5 text-slate-300">v{card.version}</span>}
        {card.comment_count > 0 && (
          <span className="px-1.5 py-0.5 rounded bg-white/5 text-slate-300 flex items-center gap-0.5">
            <MessageSquare className="w-3 h-3" />{card.comment_count}
          </span>
        )}
      </div>
    </button>
  );
}

function Lane({ lane, columns, approvers, onOpen, viewer, onDepartment }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-white/10 bg-slate-950/40 mb-3" data-testid={`sv-lane-${lane.matter_reference}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
        <button type="button" onClick={() => setOpen(!open)} className="text-slate-400 hover:text-white"
          aria-label={open ? "Fold this matter" : "Show this matter"}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Link to={`/matter/${lane.matter_reference}`} className="font-mono text-sm text-indigo-200 hover:underline">
          {lane.matter_reference}
        </Link>
        <span className="text-sm text-slate-300 truncate">{lane.client_name}</span>
        <span className="text-[11px] text-slate-500">{lane.open_count} open</span>
        {lane.ccl_agreed === false && (
          <span className="text-[11px] text-status-amber flex items-center gap-1" title="Client care letter not agreed"
            data-testid={`sv-lane-ccl-${lane.matter_reference}`}>
            <AlertTriangle className="w-3 h-3" />CCL not agreed
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400">
          {lane.department === "litigation" ? "Litigation" : "Company Commercial"}
          {lane.department_overridden && " (set by hand)"}
        </span>
        {viewer?.is_director && (
          <button type="button" className="text-[11px] text-indigo-300 hover:underline"
            data-testid={`sv-lane-flip-${lane.matter_reference}`}
            onClick={() => onDepartment(lane)}>
            {lane.department_overridden ? "Use work-type code" : `Move to ${lane.department === "litigation" ? "Company Commercial" : "Litigation"}`}
          </button>
        )}
      </div>
      {open && (
        <div className="grid grid-cols-5 divide-x divide-white/10 border-t border-white/10" data-testid="sv-lane-grid">
          {columns.map((col, i) => (
            <div key={col.key} className={`min-h-[4.5rem] p-2 ${i % 2 ? "bg-white/[0.025]" : ""}`}
              data-testid={`sv-cell-${lane.matter_reference}-${col.key}`}>
              {lane.cards.filter(c => c.column === col.key).map(c => (
                <CardTile key={c.id} card={c} approvers={approvers} onOpen={onOpen} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CardDrawer({ cardId, onClose, onChanged, approvers }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [note, setNote] = useState("");
  const [newLink, setNewLink] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/supervision/cards/${cardId}`);
      setDetail(data); setError("");
    } catch (err) {
      setError(errorMessage(err, "The card could not be opened."));
    }
  }, [cardId]);

  useEffect(() => { if (cardId) { setDetail(null); setComment(""); setNote(""); setNewLink(""); load(); } }, [cardId, load]);

  const act = async (label, fn) => {
    setBusy(label); setError("");
    try {
      await fn();
      await load();
      onChanged && onChanged();
    } catch (err) {
      setError(errorMessage(err, "That did not work."));
    } finally {
      setBusy("");
    }
  };

  const card = detail?.card;
  const decide = (decision) => act(decision, () => axios.post(`${API}/supervision/cards/${cardId}/decision`, { decision, note }).then(() => setNote("")));
  const move = (to) => act(to, () => axios.post(`${API}/supervision/cards/${cardId}/move`, { to, note, sharepoint_url: newLink }).then(() => { setNote(""); setNewLink(""); }));
  const tick = (key, ticked) => act(`tick-${key}`, () => axios.post(`${API}/supervision/cards/${cardId}/checklist`, { key, ticked }));
  const sendComment = () => act("comment", () => axios.post(`${API}/supervision/cards/${cardId}/comments`, { body: comment }).then(() => setComment("")));

  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const comments = detail?.comments || [];
  return (
    <Dialog open={!!cardId} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw]" data-testid="sv-drawer">
        <DialogHeader>
          <DialogTitle>{card ? card.title : "Opening card"}</DialogTitle>
          <DialogDescription>
            {card ? `${card.matter_reference} · ${card.client_name || ""} · ${card.owner_name} · ${card.column_label} · v${card.version}` : " "}
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-status-red text-sm" data-testid="sv-drawer-error">{error}</p>}
        {!card && !error && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
        {card && (
          <div className="grid md:grid-cols-[1fr_340px] gap-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-white/10 p-3 bg-slate-900/60">
                <div className="text-xs text-slate-400 mb-2">{isOutlookLink(card.sharepoint_url) ? "Email (in Outlook)" : "Document (in SharePoint)"}</div>
                <div className="flex flex-wrap gap-2">
                  {card.open_in_word_url ? (
                    <a href={card.open_in_word_url} data-testid="sv-open-word">
                      <Button size="sm"><FileText className="w-4 h-4 mr-1" />Open in Word</Button>
                    </a>
                  ) : isOutlookLink(card.sharepoint_url) ? null : (
                    <span className="text-[11px] text-slate-400 self-center" data-testid="sv-word-via-browser">
                      Open in browser, then Editing &gt; Open in Desktop App.
                    </span>
                  )}
                  <a href={card.sharepoint_url} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid="sv-open-browser"><ExternalLink className="w-4 h-4 mr-1" />
                      {isOutlookLink(card.sharepoint_url) ? "Open in Outlook" : "Open in browser"}</Button>
                  </a>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Line edits in Word with tracked changes; short points in the thread.</p>
              </div>
              {card.approved_by && (
                <div className="rounded-lg border border-status-green/40 bg-status-green/10 p-3 text-xs text-slate-200" data-testid="sv-approved-line">
                  Approved v{card.approved_version} by {card.approved_by_name || card.approved_by} on{" "}
                  {new Date(card.approved_at).toLocaleString("en-GB")}{card.approval_note ? <RichText text={card.approval_note} className="mt-1" /> : ""}
                </div>
              )}
              <div className="rounded-lg border border-white/10 p-3 bg-slate-900/60">
                <div className="text-xs text-slate-400 mb-1">Versions</div>
                {card.versions.map(v => (
                  <div key={v.version} className="text-xs text-slate-300 py-0.5">
                    <span className="font-semibold">v{v.version}</span>
                    {v.submitted_at && ` · ${new Date(v.submitted_at).toLocaleString("en-GB")}`}
                    {v.note && <RichText text={v.note} className="mt-0.5 text-slate-300" />}
                  </div>
                ))}
              </div>
              {detail.ccl_warning && (
                <div className="rounded-lg border border-status-amber/50 bg-status-amber/10 p-3 text-xs text-slate-200 flex gap-2"
                  data-testid="sv-ccl-warning">
                  <AlertTriangle className="w-4 h-4 text-status-amber shrink-0" />{detail.ccl_warning}
                </div>
              )}
              {detail.checklist && detail.checklist.length > 0 && (
                <div className="rounded-lg border border-white/10 p-3 bg-slate-900/60" data-testid="sv-checklist">
                  <div className="text-xs text-slate-400 mb-1 flex items-center gap-1"><Gavel className="w-3.5 h-3.5" />
                    Pre-filing checklist ({detail.checklist.filter(i => i.ticked).length}/{detail.checklist.length})
                  </div>
                  {detail.checklist.map(i => (
                    <label key={i.key} className="flex items-start gap-2 text-xs text-slate-300 py-0.5">
                      <input type="checkbox" checked={i.ticked} data-testid={`sv-check-${i.key}`}
                        disabled={!!busy || !(detail.can_decide || detail.is_owner) || ["approved", "sent"].includes(card.column)}
                        onChange={e => tick(i.key, e.target.checked)} />
                      <span>{i.label}{i.ticked && i.by ? <span className="text-slate-500"> · {i.by}</span> : null}</span>
                    </label>
                  ))}
                </div>
              )}
              {(detail.can_decide || detail.is_owner) && (
                <div className="rounded-lg border border-white/10 p-3 bg-slate-900/60 space-y-2">
                  <FormattedTextarea rows={3} value={note} onChange={setNote} testId="sv-note"
                    placeholder={detail.can_decide ? "Note (optional for Approve; Changes needed needs a comment)" : "Covering note, for example \"v2 fixes points 1 to 3\""}
                    className="w-full rounded-md border border-white/10 bg-bonsai-bg px-3 py-2 text-sm text-white" />
                  {detail.is_owner && ["changes_needed", "approved", "drafting"].includes(card.column) && (
                    <input value={newLink} onChange={e => setNewLink(e.target.value)} data-testid="sv-new-link"
                      placeholder="New SharePoint link (leave blank if it is the same file)"
                      className="w-full rounded-md border border-white/10 bg-bonsai-bg px-3 py-2 text-xs text-white" />
                  )}
                  <div className="flex flex-wrap gap-2">
                    {detail.can_decide && (
                      <>
                        <Button size="sm" onClick={() => decide("approve")} data-testid="sv-approve"
                          disabled={!!busy || (detail.checklist || []).some(i => !i.ticked)}
                          title={(detail.checklist || []).some(i => !i.ticked) ? "Tick the pre-filing checklist first" : undefined}>
                          {busy === "approve" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decide("changes_needed")} disabled={!!busy}
                          data-testid="sv-changes">
                          <Undo2 className="w-4 h-4 mr-1" />Changes needed
                        </Button>
                      </>
                    )}
                    {detail.is_owner && ["drafting", "changes_needed"].includes(card.column) && (
                      <Button size="sm" onClick={() => move("ready_for_review")} disabled={!!busy} data-testid="sv-resubmit">
                        <Send className="w-4 h-4 mr-1" />{card.column === "drafting" ? "Submit for review" : "Resubmit"}
                      </Button>
                    )}
                    {detail.is_owner && card.column === "approved" && (
                      <>
                        <Button size="sm" onClick={() => act("sent", () => axios.post(`${API}/supervision/cards/${cardId}/sent`, {}))}
                          disabled={!!busy} data-testid="sv-mark-sent">
                          <Send className="w-4 h-4 mr-1" />Mark sent
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => move("ready_for_review")} disabled={!!busy}
                          data-testid="sv-edited-after">
                          Changed it, needs approving again
                        </Button>
                      </>
                    )}
                    {detail.is_owner && ["ready_for_review", "changes_needed"].includes(card.column) && (
                      <Button size="sm" variant="outline" onClick={() => move("drafting")} disabled={!!busy}
                        data-testid="sv-to-drafting">Back to Drafting</Button>
                    )}
                  </div>
                  {card.column === "ready_for_review" && !detail.can_decide && (
                    <p className="text-[11px] text-slate-500">Only {approvers.join(", ") || "the approver"} can approve.</p>
                  )}
                </div>
              )}
              <details className="text-xs text-slate-400">
                <summary className="cursor-pointer">History ({detail.history.length})</summary>
                <ul className="mt-1 space-y-0.5" data-testid="sv-history">
                  {detail.history.map(h => (
                    <li key={h.id}>{new Date(h.at).toLocaleString("en-GB")} · {h.actor_name}: {h.summary}</li>
                  ))}
                </ul>
              </details>
            </div>
            <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3 flex flex-col min-w-0" data-testid="sv-rail">
              <div className="text-xs font-semibold text-slate-300 mb-2">Comments</div>
              <div className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden">
                {comments.length === 0 && <p className="text-xs text-slate-500">No comments yet.</p>}
                {comments.map(c => (
                  <div key={c.id} className={`rounded-md border p-2 text-xs ${c.withdrawn ? "opacity-50 border-white/5" : c.resolved ? "border-white/5 bg-white/5" : "border-indigo-400/30 bg-indigo-500/10"}`}>
                    <div className="flex items-center gap-1 text-[11px] text-slate-400">
                      <span className="font-semibold text-slate-200">{c.actor_name}</span>
                      <span>· v{c.version} · {new Date(c.at).toLocaleString("en-GB")}</span>
                    </div>
                    <RichText text={c.body} className={`mt-1 text-slate-100 ${c.withdrawn ? "line-through" : ""}`} />
                    {c.corrections?.length > 0 && <div className="text-[10px] text-slate-500 mt-1">Corrected; the original is in the history.</div>}
                    {editing === c.id ? (
                      <div className="mt-1 space-y-1">
                        <FormattedTextarea rows={4} value={editText} onChange={setEditText}
                          testId={`sv-comment-edit-text-${c.id}`}
                          className="w-full rounded-md border border-white/10 bg-bonsai-bg px-2 py-1.5 text-xs text-white" />
                        <div className="flex gap-3 text-[11px]">
                          <button type="button" className="text-indigo-300 hover:underline" disabled={!editText.trim() || !!busy}
                            data-testid={`sv-comment-save-${c.id}`}
                            onClick={() => act("edit", () => axios.post(`${API}/supervision/comments/${c.id}/correct`, { body: editText })
                              .then(() => setEditing(null)))}>Save</button>
                          <button type="button" className="text-slate-400 hover:underline" onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1 flex gap-3 text-[11px]">
                        <button type="button" className="text-indigo-300 hover:underline"
                          onClick={() => act("resolve", () => axios.post(`${API}/supervision/comments/${c.id}/resolve`, { resolved: !c.resolved }))}>
                          {c.resolved ? "Reopen" : "Resolve"}
                        </button>
                        {c.mine && (
                          <>
                            <button type="button" className="text-indigo-300 hover:underline" data-testid={`sv-comment-edit-${c.id}`}
                              onClick={() => { setEditing(c.id); setEditText(c.body); }}>Edit</button>
                            <button type="button" className="text-slate-400 hover:text-status-red hover:underline"
                              data-testid={`sv-comment-delete-${c.id}`}
                              onClick={() => window.confirm("Delete this comment? It stays in the card's history.")
                                && act("delete", () => axios.post(`${API}/supervision/comments/${c.id}/correct`, { withdraw: true }))}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <FormattedTextarea rows={4} value={comment} onChange={setComment} testId="sv-comment"
                  className="w-full rounded-md border border-white/10 bg-bonsai-bg px-2 py-1.5 text-xs text-white"
                  placeholder={"Add a comment. New lines, - for bullets, 1. for numbers, **bold**"} />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Write it as if the client will read it.</p>
              <Button size="sm" className="mt-1 self-end" onClick={sendComment} disabled={!comment.trim() || !!busy}
                data-testid="sv-comment-send">
                <MessageSquare className="w-4 h-4 mr-1" />Comment
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SupervisionBoard() {
  const [boardKey, setBoardKey] = useState("all");
  const [trainee, setTrainee] = useState("");
  const [clientFacing, setClientFacing] = useState(false);
  const [dueWeek, setDueWeek] = useState(false);
  const [waitingOnMe, setWaitingOnMe] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const params = useMemo(() => ({
    department: boardKey, trainee, client_facing_only: clientFacing, due_this_week: dueWeek,
    waiting_on_me: waitingOnMe,
  }), [boardKey, trainee, clientFacing, dueWeek, waitingOnMe]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/supervision/board`, { params });
      setData(d); setError("");
    } catch (err) {
      setError(errorMessage(err, "The board could not be loaded."));
    }
  }, [params]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const flip = async (lane) => {
    const department = lane.department_overridden ? "auto" : (lane.department === "litigation" ? "coco" : "litigation");
    try {
      await axios.post(`${API}/supervision/department`, { matter_reference: lane.matter_reference, department });
      load();
    } catch (err) {
      setError(errorMessage(err, "The matter's board could not be changed."));
    }
  };

  const columns = data?.columns || [];
  const approvers = data?.approvers || [];
  return (
    <div className="p-4 md:p-6" data-testid="supervision-board-page">
      <PageHero icon={ClipboardCheck} title="Supervision board" testId="supervision-board-hero"
        subtitle={`Outgoing trainee work, per matter. Target: ${data?.turnaround_working_days ?? 2} working days in Ready for review.`}>
        <SubmitForReviewDialog onSubmitted={load} trigger={open => (
          <Button size="sm" onClick={open} data-testid="sv-new-card"><Plus className="w-4 h-4 mr-1" />Submit for review</Button>
        )} />
      </PageHero>

      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
        <div className="flex rounded-lg border border-white/10 overflow-hidden" role="tablist">
          {BOARDS.map(b => (
            <button key={b.key} type="button" role="tab" aria-selected={boardKey === b.key}
              onClick={() => setBoardKey(b.key)} data-testid={`sv-board-${b.key}`}
              className={`px-3 py-1.5 ${boardKey === b.key ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-white/5"}`}>
              {b.label}
            </button>
          ))}
        </div>
        <select value={trainee} onChange={e => setTrainee(e.target.value)} data-testid="sv-filter-trainee"
          className="rounded-md border border-white/10 bg-bonsai-bg px-2 py-1.5 text-sm text-white">
          <option value="">Everyone</option>
          {(data?.trainees || []).map(t => <option key={t.email} value={t.email}>{t.name}</option>)}
        </select>
        {[["Client-facing only", clientFacing, setClientFacing, "client"],
          ["Due this week", dueWeek, setDueWeek, "due"],
          ["Waiting on me", waitingOnMe, setWaitingOnMe, "mine"]].map(([label, on, set, id]) => (
          <button key={id} type="button" onClick={() => set(!on)} aria-pressed={on} data-testid={`sv-filter-${id}`}
            className={`px-2.5 py-1.5 rounded-md border text-xs ${on ? "border-indigo-400 bg-indigo-500/20 text-white" : "border-white/10 text-slate-300"}`}>
            {label}
          </button>
        ))}
        {data && !data.viewer?.sees_all && (
          <span className="text-xs text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />Showing only matters you are on</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-slate-400" data-testid="sv-legend">
        {Object.entries(TYPE_LABELS).map(([k, t]) => (
          <span key={k} className="flex items-center gap-1"><span className={`h-2 w-6 rounded-full ${t.cls}`} />{t.label}</span>
        ))}
        <span className="flex items-center gap-1"><span className="h-2 w-6 rounded-full bg-cyan-400" />Client-facing</span>
      </div>
      {error && <p className="text-status-red text-sm mb-3" data-testid="sv-error">{error}</p>}
      {!data && !error && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}

      {data && (
        <>
          <div className="grid grid-cols-5 divide-x divide-white/10 rounded-xl border border-white/10 bg-slate-950 mb-3 sticky top-0 z-10 overflow-hidden">
            {columns.map((c, i) => (
              <div key={c.key} className={`text-xs font-semibold uppercase tracking-wide text-slate-300 flex items-center gap-1.5 px-3 py-2.5 border-t-4 ${(COLUMN_HUES[c.key] || {}).top || ""} ${i % 2 ? "bg-white/[0.025]" : ""}`}
                data-testid={`sv-col-${c.key}`}>
                <span className={`w-2.5 h-2.5 rounded-sm ${(COLUMN_HUES[c.key] || {}).head || ""}`} />
                {c.label}<span className="text-slate-500 font-normal">({data.counts[c.key] || 0})</span>
              </div>
            ))}
          </div>
          {data.swimlanes.length === 0 && (
            <div className="rounded-xl border border-white/10 p-6 text-center text-sm text-slate-400" data-testid="sv-empty">
              Nothing on this board. Use Submit for review to add a piece of work.
            </div>
          )}
          {orderLanes(data.swimlanes).map(lane => (
            <Lane key={lane.matter_reference} lane={lane} columns={columns} approvers={approvers}
              viewer={data.viewer} onOpen={c => setOpenId(c.id)} onDepartment={flip} />
          ))}
        </>
      )}
      <CardDrawer cardId={openId} onClose={() => setOpenId(null)} onChanged={load} approvers={approvers} />
    </div>
  );
}

